/**
 * SaveWorkDialog — guards the "New Project" flow when a project is open.
 *
 * Shows when a writer clicks File → New Project while they have an existing
 * project loaded.  Gives them two paths:
 *
 *   1. "Save to current folder" — triggers a DOCX download of the open
 *      project (same mechanism as Save As → Device), then opens the
 *      New Project dialog so they can start fresh.
 *
 *   2. "Open New Project window" — skips saving and goes straight to the
 *      New Project dialog.  The writer's work is already auto-saved to
 *      the cloud, so this is safe.
 *
 *   Cancel — dismisses without touching anything.
 */
import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button }    from '@/components/ui/button';
import { toast }     from 'sonner';
import { cn }        from '@/lib/utils';
import {
  FolderOpen, PlusCircle, CloudCheck, Loader2, FileDown,
} from 'lucide-react';
import { exportExtrasApi } from '@/lib/api';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

// ── small choice card ─────────────────────────────────────────────────────
function ChoiceCard({ icon: Icon, title, description, detail, onClick, disabled, loading, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={cn(
        'group relative w-full rounded-xl border border-border bg-card text-left',
        'px-4 py-4 transition-all duration-150',
        'hover:border-primary/60 hover:bg-primary/5 hover:shadow-sm',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60',
        'disabled:pointer-events-none disabled:opacity-50',
      )}
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          {loading
            ? <Loader2 className="h-4 w-4 animate-spin" />
            : <Icon className="h-4 w-4" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13.5px] font-semibold leading-snug">{title}</p>
          <p className="mt-0.5 text-[12px] text-muted-foreground leading-relaxed">{description}</p>
          {detail && (
            <p className="mt-1.5 inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 font-mono text-[10.5px] text-muted-foreground">
              <FolderOpen className="h-3 w-3 shrink-0" />
              <span className="truncate max-w-[220px]">{detail}</span>
            </p>
          )}
        </div>
        <svg
          xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
          fill="none" stroke="currentColor" strokeWidth="2.5"
          strokeLinecap="round" strokeLinejoin="round"
          className="mt-1 shrink-0 text-muted-foreground/40 transition-colors group-hover:text-primary"
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </div>
    </button>
  );
}

// ── main component ────────────────────────────────────────────────────────
export default function SaveWorkDialog({ open, onOpenChange, project, onProceed }) {
  const [saving, setSaving] = useState(false);

  const projectName   = project?.name || project?.title || 'Untitled';
  const folderLabel   = project?.folder || 'Default working folder';

  // ── download DOCX then proceed to New Project ─────────────────────────
  const handleSaveAndContinue = async () => {
    if (!project?.id) { onProceed(); onOpenChange(false); return; }
    setSaving(true);
    try {
      // Reuse the same export URL the menu "Save As → Device" uses
      const url = exportExtrasApi.manuscriptUrl
        ? exportExtrasApi.manuscriptUrl(project.id, 'docx')
        : `${API}/projects/${project.id}/export/manuscript.docx`;

      const a       = document.createElement('a');
      a.href        = url;
      a.download    = `${projectName.replace(/[^a-z0-9_\-. ]/gi, '_')}.docx`;
      a.target      = '_blank';
      a.rel         = 'noopener';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success(`Downloading "${projectName}.docx" to your device`);
    } catch {
      toast.error('Download failed — you can still export later from File → Export.');
    } finally {
      setSaving(false);
      onOpenChange(false);
      onProceed();      // open NewProjectDialog regardless
    }
  };

  // ── skip saving, go straight to New Project ──────────────────────────
  const handleSkipAndContinue = () => {
    onOpenChange(false);
    onProceed();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[460px] gap-5"
        data-testid="save-work-dialog"
      >
        <DialogHeader className="space-y-1.5">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-500/15 text-amber-500">
              <CloudCheck className="h-4 w-4" />
            </div>
            <DialogTitle className="text-[15px] font-semibold">
              Save your work before moving on?
            </DialogTitle>
          </div>
          <DialogDescription className="text-[12.5px] leading-relaxed pl-10">
            <span className="font-medium text-foreground">&ldquo;{projectName}&rdquo;</span> is currently
            open. Your writing is already auto-saved to the cloud, but you can also download a local
            backup before starting a new project.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2.5" data-testid="save-work-choices">
          <ChoiceCard
            icon={FileDown}
            title="Save to current working folder"
            description="Download a DOCX backup of this project to your device, then open the New Project window."
            detail={folderLabel}
            onClick={handleSaveAndContinue}
            disabled={saving}
            loading={saving}
            testId="save-work-save-btn"
          />
          <ChoiceCard
            icon={PlusCircle}
            title="Create a new working folder"
            description="Go straight to the New Project window. You can choose or create a folder for the new project there."
            onClick={handleSkipAndContinue}
            disabled={saving}
            testId="save-work-skip-btn"
          />
        </div>

        <div className="flex justify-end border-t border-border pt-1">
          <Button
            variant="ghost"
            size="sm"
            className="text-[12px]"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            data-testid="save-work-cancel"
          >
            Cancel — stay in this project
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
